import React, { useEffect, useRef, useState } from "react";
import EncordLogoDarkMode from "../assets/icons/EncordLogoDarkMode.svg";
import EncordLogoLightMode from "../assets/icons/EncordLogoLightMode.svg";
import CustomButton from "../components/CustomButton.jsx";
import DemoModal from "../components/DemoModal.jsx";
import Transition from "../utilities/Transition.js";
import CompanyHeaderButton, { company } from "./CompanyHeaderButton";
import ProductHeaderButton from "./ProductHeaderButton";
import ResourcesHeaderButton from "./ResourcesHeaderButton.jsx";
import { SHOW_BANNER_PAGES } from "../constants/index.jsx";
import CustomNewsBanner from "../components/CustomNewBanner.jsx";
import { Link } from "gatsby";
import aiDataChatsPng from "../assets/images/jen-ai-data-chats-banner.png";
import aiAfterHoursPng from "../assets/images/ai-after-hours-banner.png";

const buildVsBuySvg = "/build-vs-buy-banner-mobile.svg";

export const headerTheme = {
  default: {
    background: "bg-white",
    logo: <EncordLogoLightMode />,
    textColor: "text-gray-2500",
    hoverTextColor: "text-purple-1100",
    loginBtn:
      "mr-3 text-base font-manrope text-gray-2500 font-semibold lg:px-4 lg:py-2 rounded-full border border-gray-2200 hover:border-gray-2500 focus:border-gray-2500",
    icon: "gray",
    hoverIcon: "grayHover",
    bookDemo:
      "text-sm sm:text-base font-manrope font-semibold bg-purple-1500 w-full text-white sm:w-auto px-7 py-2.5 lg:py-2 lg:px-4 rounded-full hover:bg-[#5E24FF]",
  },
  purple: {
    background: "bg-[#210b47f5]",
    logo: <EncordLogoDarkMode />,
    textColor: "text-white",
    hoverTextColor: "text-white",
    loginBtn:
      "mr-3 text-base font-manrope text-purple-2000 bg-white font-semibold lg:px-4 lg:py-2 rounded-full",
    icon: "white",
    hoverIcon: "white",
    bookDemo:
      "text-sm sm:text-base outline_btn font-semibold w-full sm:w-auto lg:py-2 lg:px-4 rounded-full bg-white text-purple-1500",
  },
  purpleWithoutOpacity: {
    background: "bg-[#1E0842] md:!bg-opacity-100",
    logo: <EncordLogoDarkMode />,
    textColor: "text-white",
    hoverTextColor: "text-white",
    loginBtn:
      "mr-3 text-base font-manrope text-[#1E0842] bg-white font-semibold lg:px-4 lg:py-2 rounded-full border border-gray-2200 hover:border-gray-2500 focus:border-gray-2500",
    icon: "white",
    hoverIcon: "white",
    bookDemo:
      "text-sm sm:text-base font-manrope outline_btn font-semibold w-full sm:w-auto lg:py-2 lg:px-4 rounded-full itk-btn_dark text-white text-purple-2000",
  },
  newDefault: {
    background: "bg-gray-2800",
    logo: <EncordLogoLightMode />,
    textColor: "text-gray-2500",
    hoverTextColor: "text-purple-1500",
    loginBtn:
      "mr-3 text-base font-manrope text-gray-2500 font-semibold lg:px-4 lg:py-2 rounded-full border border-gray-2200 hover:border-gray-2500 focus:border-gray-2500",
    icon: "gray",
    hoverIcon: "purpleHover",
    bookDemo:
      "text-sm sm:text-base font-manrope font-semibold bg-purple-1500 w-full text-white sm:w-auto px-7 py-2.5 lg:py-2 lg:px-4 rounded-full !duration-0 hover:bg-[#5E24FF]",
  },
  transparent: {
    background: "bg-transparent",
    logo: <EncordLogoDarkMode />,
    textColor: "text-white",
    hoverTextColor: "text-purple-1500",
    headerHoverTextColor: "text-gray-2500",
    loginBtn:
      "mr-3 text-base font-manrope text-[#1E0842] bg-white font-semibold lg:px-4 lg:py-2 rounded-full border border-gray-2200 hover:border-gray-2500 focus:border-gray-2500",
    icon: "white",
    hoverIcon: "purpleHover",
    headerHoverIcon: "gray",
    bookDemo:
      "text-sm sm:text-base font-manrope outline_btn font-semibold w-full sm:w-auto lg:py-2 lg:px-4 rounded-full itk-btn_dark !duration-0 text-white text-purple-1500",
    topBackground: "bg-transparent",
  },
  menu: {
    background: "bg-transparent",
    logo: <EncordLogoDarkMode />,
    textColor: "text-white",
    hoverTextColor: "text-white",
    loginBtn:
      "mr-3 text-base font-manrope text-[#1E0842] bg-white font-semibold lg:px-4 lg:py-2 rounded-full border border-gray-2200 hover:border-gray-2500 focus:border-gray-2500",
    icon: "white",
    hoverIcon: "white",
    bookDemo:
      "text-sm sm:text-base font-manrope outline_btn font-semibold w-full sm:w-auto lg:py-2 lg:px-4 rounded-full itk-btn_dark !duration-0 text-white text-purple-1500",
    topBackground: "bg-gray-2800",
  },
};

const engageItems = [
  {
    id: "ai-data-chats",
    name: "AI Data Chats",
    description:
      "Watch Jen's series of interviews with key thought leaders in the AI data space.",
    buttonText: "Watch all episodes",
    href: "/ai-data-chats/",
  },
  {
    id: "ai-after-hours",
    name: "AI After Hours",
    description:
      "Monthly meetups featuring lightning talks from AI leaders and industry experts.",
    buttonText: "See all events",
    href: "/ai-after-hours/",
  },
  {
    id: "build-vs-buy",
    name: "Build vs Buy Calculator",
    description:
      "Use Encord's ROI calculator and learn how your team can achieve xyz.",
    buttonText: "Start calculating ROI",
    href: "/build-vs-buy-calculator/",
  },
];

const AIDataChatsImage = () => (
  <img
    src={aiDataChatsPng}
    alt="AI Data Chats"
    className="w-full h-full object-cover rounded"
    loading="lazy"
    decoding="async"
  />
);

const AIAfterHoursImage = () => (
  <img
    src={aiAfterHoursPng}
    alt="AI After Hours"
    className="w-full h-full object-cover rounded"
    loading="lazy"
    decoding="async"
  />
);

const BuildVsBuyImage = () => (
  <img
    src={buildVsBuySvg}
    alt="Build vs Buy Calculator"
    className="w-full h-full object-cover rounded"
    loading="lazy"
    decoding="async"
  />
);

const ChevronDownIcon = ({ className = "", isRotated = false }) => (
  <svg
    className={`w-7 h-7 transition-transform duration-200 ${isRotated ? "-rotate-90 scale-y-[-1]" : "rotate-90"} ${className}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
    viewBox="0 0 20 20"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 6l4 4-4 4" />
  </svg>
);

const CarouselIndicators = ({ total, current, onIndicatorClick }) => (
  <div className="flex justify-center gap-3 pt-6">
    {Array.from({ length: total }, (_, index) => (
      <button
        key={index}
        onClick={() => onIndicatorClick(index)}
        className={`h-1.5 rounded-full transition-all duration-200 ${
          index === current
            ? "w-8 bg-purple-1500"
            : "w-3 bg-gray-400 hover:bg-gray-500"
        }`}
      />
    ))}
  </div>
);

const Header = ({
  location,
  setIsMobileMenuOpen = null,
  theme = "default",
}) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const [mobileExpandedSections, setMobileExpandedSections] = useState({
    platform: false,
    learn: false,
    company: false,
  });
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const [top, setTop] = useState(true);
  const [isProductHover, setIsProductHover] = useState(false);
  const [isResourceHover, setIsResourceHover] = useState(false);
  const [isCompanyHover, setIsCompanyHover] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pricingHover, setPricingHover] = useState(false);
  const [customerHover, setCustomerHover] = useState(false);
  const [loginHover, setLoginHover] = useState(false);
  const [headerHovered, setHeaderHovered] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);

  const mobileNav = useRef(null);
  const companyRef = useRef();
  const productRef = useRef();
  const resourceRef = useRef();
  const timeoutRef = useRef(null);

  const handleMenuMouseEnter = (menuType) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsProductHover(false);
    setIsResourceHover(false);
    setIsCompanyHover(false);

    if (menuType === "product") setIsProductHover(true);
    if (menuType === "resource") setIsResourceHover(true);
    if (menuType === "company") setIsCompanyHover(true);
  };

  const handleMenuMouseLeave = (menuType) => {
    timeoutRef.current = setTimeout(() => {
      if (menuType === "product") setIsProductHover(false);
      if (menuType === "resource") setIsResourceHover(false);
      if (menuType === "company") setIsCompanyHover(false);
      timeoutRef.current = null;
    }, 250);
  };

  const handlepricingEnter = () => setPricingHover(true);
  const handlepricingLeave = () => setPricingHover(false);
  const handleCustomerEnter = () => setCustomerHover(true);
  const handleCustomerLeave = () => setCustomerHover(false);
  const handleLoginEnter = () => setLoginHover(true);
  const handleLoginLeave = () => setLoginHover(false);
  const handleHeaderEnter = () => setHeaderHovered(true);
  const handleHeaderLeave = () => setHeaderHovered(false);

  // Helper function to get the appropriate text color for transparent theme
  const getTextColor = () => {
    if (
      theme === "transparent" &&
      (headerHovered || isProductHover || isResourceHover || isCompanyHover)
    ) {
      return headerTheme[theme].headerHoverTextColor;
    }
    return headerTheme[theme].textColor;
  };

  // Helper function to get the appropriate icon color for transparent theme
  const getIconColor = () => {
    if (
      theme === "transparent" &&
      (headerHovered || isProductHover || isResourceHover || isCompanyHover)
    ) {
      return headerTheme[theme].headerHoverIcon;
    }
    return headerTheme[theme].icon;
  };

  // Helper function to get the appropriate logo for transparent theme
  const getLogo = () => {
    // For transparent theme, determine if header has white background
    if (theme === "transparent") {
      // Show light mode logo when header has white background:
      // 1. Mobile menu is open (header gets bg-white)
      // 2. Header hovered or any dropdown menu is open on hover-capable devices
      if (
        mobileNavOpen ||
        ((headerHovered ||
          isProductHover ||
          isResourceHover ||
          isCompanyHover) &&
          supportsHover)
      ) {
        return <EncordLogoLightMode />;
      }
      // Otherwise use dark mode logo for transparent background
      return <EncordLogoDarkMode />;
    }
    return headerTheme[theme].logo;
  };

  // New mobile menu handlers
  const toggleMobileSection = (section) => {
    setMobileExpandedSections((prev) => {
      const newState = {
        ...prev,
        [section]: !prev[section],
      };

      // Auto-scroll to section when expanding, especially for company section at bottom
      if (newState[section]) {
        setTimeout(() => {
          const sectionElement = document.querySelector(
            `[data-section="${section}"]`,
          );
          if (sectionElement) {
            sectionElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }
        }, 100); // Small delay to allow DOM update
      }

      return newState;
    });
  };

  const handleCarouselSwipe = (direction) => {
    if (direction === "next") {
      setCurrentCarouselIndex((prev) =>
        prev === engageItems.length - 1 ? 0 : prev + 1,
      );
    } else {
      setCurrentCarouselIndex((prev) =>
        prev === 0 ? engageItems.length - 1 : prev - 1,
      );
    }
  };

  const handleCarouselScroll = (e) => {
    if (isScrolling) return;

    // Only respond to horizontal scrolling (deltaX)
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 10) {
      e.preventDefault();
      setIsScrolling(true);

      if (e.deltaX > 0) {
        handleCarouselSwipe("next");
      } else {
        handleCarouselSwipe("prev");
      }

      // Debounce scroll events
      setTimeout(() => {
        setIsScrolling(false);
      }, 300);
    }
  };
  const renderCarouselImage = (item) => {
    switch (item.id) {
      case "ai-after-hours":
        return <AIAfterHoursImage />;
      case "build-vs-buy":
        return <BuildVsBuyImage />;
      default:
        return <AIDataChatsImage />;
    }
  };

  // Detect if device supports hover
  useEffect(() => {
    if (typeof window !== "undefined") {
      setSupportsHover(window.matchMedia("(hover: hover)").matches);
    }
  }, []);

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!mobileNavOpen || mobileNav.current.contains(target)) return;
      setMobileNavOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, []);

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!mobileNavOpen || keyCode !== 27) return;
      setMobileNavOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, []);

  // Prevent body scroll when mobile nav is open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const scrollHandler = () => {
      window.pageYOffset > 10 ? setTop(false) : setTop(true);
    };
    window.addEventListener("scroll", scrollHandler);
    return () => window.removeEventListener("scroll", scrollHandler);
  }, [top]);

  const closeModal = () => setIsOpen(false);

  return (
    <header
      className={`z-50 backdrop-blur-[10px] lg:backdrop-blur-[0px] fixed w-full hover:!text-slate-600 md:bg-opacity-90 transition duration-300 ease-in-out ${
        mobileNavOpen
          ? theme === "transparent"
            ? `bg-white`
            : `bg-gray-white lg:bg-transparent `
          : `${
              top
                ? `${headerTheme[theme].topBackground}${
                    theme === "transparent" &&
                    supportsHover &&
                    (headerHovered ||
                      isProductHover ||
                      isResourceHover ||
                      isCompanyHover)
                      ? " !bg-white"
                      : ""
                  }`
                : `shadow-lg ${headerTheme[theme].background} !backdrop-blur-[10px]`
            }`
      } `}
      onMouseEnter={supportsHover ? handleHeaderEnter : undefined}
      onMouseLeave={supportsHover ? handleHeaderLeave : undefined}
    >
      {SHOW_BANNER_PAGES.includes(location?.pathname) && (
        <CustomNewsBanner
          location={location}
          localStorageVariableName="wednesdayWebinar"
        />
      )}
      <div className="z-10 px-5 mx-auto bg-transparent max-w-7xl sm:px-6 xl:px-16">
        <div className="flex items-center justify-between py-4 max-h-[72px] lg:py-[15px]">
          {/* Site branding */}
          <div className="flex-shrink-0 mr-4">
            <Link arialabel="encord-logo" to="/" className="block">
              {getLogo()}
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex md:flex-grow">
            {/* Desktop menu links */}
            <ul className="flex items-center justify-end flex-grow flex-shrink-0 gap-y-12 gap-x-5 lgxl_mid:gap-x-7.5 flex-nowrap">
              {/* 1st level: hover */}
              <li>
                <ProductHeaderButton
                  theme={theme}
                  handleEnter={() => handleMenuMouseEnter("product")}
                  handleLeave={() => handleMenuMouseLeave("product")}
                  ref={productRef}
                  isHover={isProductHover}
                  textColor={getTextColor()}
                  iconColor={getIconColor()}
                />
              </li>

              <li
                onMouseEnter={handleCustomerEnter}
                onMouseLeave={handleCustomerLeave}
              >
                <Link
                  to="/customers/"
                  className={`inline-flex items-center text-sm lgxl_mid:text-base font-medium ${customerHover ? `${headerTheme[theme].hoverTextColor}` : `${getTextColor()}`}  focus:outline-none  `}
                >
                  Customers
                </Link>
              </li>

              <li
                onMouseEnter={handlepricingEnter}
                onMouseLeave={handlepricingLeave}
              >
                <Link
                  to="/pricing/"
                  className={`inline-flex items-center text-sm lgxl_mid:text-base font-medium ${pricingHover ? `${headerTheme[theme].hoverTextColor}` : `${getTextColor()}`}  focus:outline-none `}
                >
                  Pricing
                </Link>
              </li>

              <li>
                <ResourcesHeaderButton
                  theme={theme}
                  handleEnter={() => handleMenuMouseEnter("resource")}
                  handleLeave={() => handleMenuMouseLeave("resource")}
                  ref={resourceRef}
                  isHover={isResourceHover}
                  textColor={getTextColor()}
                  iconColor={getIconColor()}
                />
              </li>
              <li>
                <CompanyHeaderButton
                  theme={theme}
                  handleEnter={() => handleMenuMouseEnter("company")}
                  handleLeave={() => handleMenuMouseLeave("company")}
                  ref={companyRef}
                  isHover={isCompanyHover}
                  textColor={getTextColor()}
                  iconColor={getIconColor()}
                />
              </li>
              <li
                onMouseEnter={handleLoginEnter}
                onMouseLeave={handleLoginLeave}
              >
                <a
                  href="https://app.encord.com/login"
                  className={`font-medium text-sm lgxl_mid:text-base ${loginHover ? `${headerTheme[theme].hoverTextColor}` : `${getTextColor()}`}`}
                >
                  Login
                </a>
              </li>
            </ul>

            {/* Desktop sign in links */}
            <ul className="flex flex-wrap items-center justify-end flex-grow gap-2 lgxl_mid:gap-3">
              <li>
                <Link
                  className="flex bg-white hover:border-gray-2500 text-gray-2500 border border-gray-2200 mx-0 whitespace-nowrap w-full lg:w-auto max-w-max group/button items-center justify-center py-2 px-3 lgxl_mid:px-4 font-semibold ease-in-out duration-500 rounded-full font-manrope tracking-tight_1"
                  to="/ai-expert"
                  style={{ outline: "none" }}
                >
                  Speak to an AI expert
                </Link>
              </li>

              <li>
                <CustomButton
                  text="Book a demo"
                  buttonClass={headerTheme[theme].bookDemo}
                  link="/book-demo"
                  id="header-book-a-demo-dekstop-cta"
                />
              </li>
            </ul>
          </nav>

          {/* Mobile menu */}
          <div className="flex lg:hidden">
            {/* Hamburger button - KEEP THE SAME */}
            <button
              className={`hamburger ${mobileNavOpen && "active"}`}
              aria-controls="mobile-nav"
              aria-expanded={mobileNavOpen}
              onClick={() => {
                setMobileNavOpen(!mobileNavOpen);
                setIsMobileMenuOpen && setIsMobileMenuOpen(!mobileNavOpen);
              }}
            >
              <span className="sr-only">Menu</span>
              <svg
                role="presentation"
                className={`w-6 h-6 ${getTextColor()} fill-current`}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  fill:
                    !mobileNavOpen && theme === "transparent"
                      ? headerHovered
                        ? "#6B7280"
                        : "white"
                      : "black",
                }}
              >
                <rect y="4" width="24" height="2" />
                <rect y="11" width="24" height="2" />
                <rect y={mobileNavOpen ? "11" : "18"} width="24" height="2" />
              </svg>
            </button>

            {/* NEW Mobile navigation content */}
            <div ref={mobileNav}>
              <Transition
                show={mobileNavOpen}
                tag="nav"
                id="mobile-nav"
                className="absolute left-0 z-20 w-full h-screen transition-all duration-300 ease-in-out bg-white top-full overflow-hidden"
                enter="transition ease-out duration-200 transform"
                enterStart="opacity-0 -translate-y-2"
                enterEnd="opacity-100 translate-y-0"
                leave="transition ease-out duration-200"
                leaveStart="opacity-100"
                leaveEnd="opacity-0"
              >
                <div className="flex flex-col h-[calc(100svh-64px)]">
                  {/* Scrollable Content */}
                  <div
                    className="flex-1 overflow-y-auto scrollbar-hide"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                  >
                    <style jsx>{`
                      .scrollbar-hide::-webkit-scrollbar {
                        display: none;
                      }
                    `}</style>

                    {/* Platform Section */}
                    <div
                      className="bg-white border-b border-gray-1600"
                      data-section="platform"
                    >
                      <button
                        onClick={() => toggleMobileSection("platform")}
                        className="w-full px-4 py-[18px] flex items-center justify-between text-left"
                      >
                        <span
                          className={`text-lg font-medium tracking-[-1px] leading-7 ${
                            mobileExpandedSections.platform
                              ? "text-[#5e44ff]"
                              : "text-[#595959]"
                          }`}
                        >
                          Platform
                        </span>
                        <ChevronDownIcon
                          isRotated={mobileExpandedSections.platform}
                          className={
                            mobileExpandedSections.platform
                              ? "text-[#5e44ff]"
                              : "text-gray-600"
                          }
                        />
                      </button>

                      {mobileExpandedSections.platform && (
                        <div className="bg-[#fafafa]">
                          {/* Product Section */}
                          <div className="px-4 pt-3.5 pb-6">
                            <h3 className="text-[#aaaaaa] text-base font-medium leading-6 tracking-[-0.2px] mb-[14px]">
                              Product
                            </h3>
                            <div className="space-y-5">
                              <Link
                                to="/encord-index/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Data management & curation
                              </Link>
                              <Link
                                to="/annotate/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Data annotation
                              </Link>
                              <Link
                                to="/active/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Model evaluation
                              </Link>
                              <Link
                                to="/data-agents/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Data agents
                              </Link>
                              <Link
                                to="/accelerate/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Labeling services
                              </Link>
                            </div>
                          </div>

                          {/* Solution Section */}
                          <div className="px-4 pt-3.5 pb-6">
                            <h3 className="text-[#aaaaaa] text-base font-medium leading-6 tracking-[-0.2px] mb-[14px]">
                              Solution
                            </h3>
                            <div className="space-y-5">
                              <Link
                                to="/physical-ai/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Physical AI
                              </Link>
                            </div>
                          </div>

                          {/* Modalities Section */}
                          <div className="px-4 pt-3.5 pb-6">
                            <h3 className="text-[#aaaaaa] text-base font-medium leading-6 tracking-[-0.2px] mb-[14px]">
                              Modalities
                            </h3>
                            <div className="space-y-5">
                              <Link
                                to="/multimodal/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Multimodal
                              </Link>
                              <Link
                                to="/image/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Image
                              </Link>
                              <Link
                                to="/video/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Video
                              </Link>
                              <Link
                                to="/document/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Document & text
                              </Link>
                              <Link
                                to="/audio/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Audio
                              </Link>
                              <Link
                                to="/dicom/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                DICOM & NIfTI
                              </Link>
                              <Link
                                to="/lidar/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                LiDAR
                              </Link>
                            </div>
                          </div>

                          {/* Security Section */}
                          <div className="px-4 pt-3.5 pb-6">
                            <h3 className="text-[#aaaaaa] text-base font-medium leading-6 tracking-[-0.2px] mb-[14px]">
                              Security and governance
                            </h3>
                            <div className="space-y-5">
                              <Link
                                to="/security/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                Security
                              </Link>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Customers */}
                    <div className="bg-white border-b border-gray-1600 px-4 py-[18px]">
                      <Link
                        to="/customers/"
                        className="text-lg font-medium text-[#595959] tracking-[-1px] leading-7 hover:text-gray-800"
                      >
                        Customers
                      </Link>
                    </div>

                    {/* Pricing */}
                    <div className="bg-white border-b border-gray-1600 px-4 py-[18px]">
                      <Link
                        to="/pricing/"
                        className="text-lg font-medium text-[#595959] tracking-[-1px] leading-7 hover:text-gray-800"
                      >
                        Pricing
                      </Link>
                    </div>

                    {/* Learn Section */}
                    <div
                      className="bg-white border-b border-gray-1600"
                      data-section="learn"
                    >
                      <button
                        onClick={() => toggleMobileSection("learn")}
                        className="w-full px-4 py-[18px] flex items-center justify-between text-left"
                      >
                        <span
                          className={`text-lg font-medium tracking-[-1px] leading-7 ${
                            mobileExpandedSections.learn
                              ? "text-[#5e44ff]"
                              : "text-[#595959]"
                          }`}
                        >
                          Learn
                        </span>
                        <ChevronDownIcon
                          isRotated={mobileExpandedSections.learn}
                          className={
                            mobileExpandedSections.learn
                              ? "text-[#5e44ff]"
                              : "text-gray-600"
                          }
                        />
                      </button>

                      {mobileExpandedSections.learn && (
                        <div className="bg-gray-50">
                          {/* Resources Section */}
                          <div className="px-4 pt-3.5 pb-6">
                            <h3 className="text-[#aaaaaa] text-sm font-medium leading-5 tracking-[-0.35px] mb-5">
                              Resources
                            </h3>
                            <div className="space-y-5">
                              <Link
                                to="/blog/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-0.9px] hover:text-gray-800"
                              >
                                Blog
                              </Link>
                              <a
                                href="https://docs.encord.com/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-0.9px] hover:text-gray-800"
                              >
                                Documentation
                              </a>
                              <Link
                                to="/research-reports/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-0.9px] hover:text-gray-800"
                              >
                                Research papers
                              </Link>
                              <Link
                                to="/glossary/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-0.9px] hover:text-gray-800"
                              >
                                Glossary
                              </Link>
                              <Link
                                to="/learning-hub/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-0.9px] hover:text-gray-800"
                              >
                                Learning hub
                              </Link>
                              <Link
                                to="/webinars/"
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-0.9px] hover:text-gray-800"
                              >
                                Webinars
                              </Link>
                            </div>
                          </div>

                          {/* Engage Carousel Section */}
                          <div className="bg-[#f2f3f5] pt-0 pb-11">
                            <div className="px-4 pt-3.5 pb-[18px]">
                              <h3 className="text-[#aaaaaa] text-sm font-medium leading-5 tracking-[-0.35px]">
                                Engage
                              </h3>
                            </div>

                            {/* Carousel Container */}
                            <div className="overflow-hidden relative px-4">
                              <div
                                className="flex transition-transform duration-300 ease-in-out"
                                style={{
                                  transform: `translateX(-${(() => {
                                    const containerWidth =
                                      typeof window !== "undefined"
                                        ? window.innerWidth - 32
                                        : 320;
                                    const itemWidth = 288;
                                    const totalWidth =
                                      engageItems.length * itemWidth;
                                    const maxTransform = Math.max(
                                      0,
                                      totalWidth - containerWidth + 32,
                                    );
                                    return Math.min(
                                      currentCarouselIndex * itemWidth,
                                      maxTransform,
                                    );
                                  })()}px)`,
                                  width: `${engageItems.length * 288}px`,
                                }}
                                onTouchStart={(e) => {
                                  const touchStartX = e.touches[0].clientX;
                                  e.currentTarget.setAttribute(
                                    "data-touch-start",
                                    touchStartX,
                                  );
                                }}
                                onTouchEnd={(e) => {
                                  const touchStartX = parseFloat(
                                    e.currentTarget.getAttribute(
                                      "data-touch-start",
                                    ),
                                  );
                                  const touchEndX = e.changedTouches[0].clientX;
                                  const diff = touchStartX - touchEndX;

                                  if (Math.abs(diff) > 50) {
                                    // Minimum swipe distance
                                    if (diff > 0) {
                                      handleCarouselSwipe("next");
                                    } else {
                                      handleCarouselSwipe("prev");
                                    }
                                  }
                                }}
                                onWheel={handleCarouselScroll}
                              >
                                {engageItems.map((item, index) => (
                                  <div
                                    key={item.id}
                                    className="w-72 flex-shrink-0 flex flex-col gap-2.5 mr-4"
                                  >
                                    {/* Image */}
                                    <div className="w-72 h-72 relative">
                                      {renderCarouselImage(item)}
                                    </div>

                                    {/* Content */}
                                    <div className="px-2">
                                      <div className="space-y-3.5">
                                        <div className="space-y-1">
                                          <h4 className="text-gray-800 text-lg font-medium leading-7 tracking-[-1px]">
                                            {item.name}
                                          </h4>
                                          <p className="text-gray-800 text-xs leading-4 tracking-[-0.3px]">
                                            {item.description}
                                          </p>
                                        </div>

                                        <Link
                                          to={item.href}
                                          className="block w-full bg-gray-50 border border-[#bfbfbf] rounded-[40px] px-4 py-2 text-center hover:bg-white transition-colors"
                                        >
                                          <span
                                            className="text-[#434343] text-base font-semibold leading-6 tracking-[-0.4px]"
                                            style={{
                                              fontFamily: "Manrope, sans-serif",
                                            }}
                                          >
                                            {item.buttonText}
                                          </span>
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Carousel Indicators */}
                              <CarouselIndicators
                                total={engageItems.length}
                                current={currentCarouselIndex}
                                onIndicatorClick={setCurrentCarouselIndex}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Company Section */}
                    <div
                      className="bg-white border-b border-gray-1600"
                      data-section="company"
                    >
                      <button
                        onClick={() => toggleMobileSection("company")}
                        className="w-full px-4 py-[18px] flex items-center justify-between text-left"
                      >
                        <span
                          className={`text-lg font-medium tracking-[-1px] leading-7 ${
                            mobileExpandedSections.company
                              ? "text-[#5e44ff]"
                              : "text-[#595959]"
                          }`}
                        >
                          Company
                        </span>
                        <ChevronDownIcon
                          isRotated={mobileExpandedSections.company}
                          className={
                            mobileExpandedSections.company
                              ? "text-[#5e44ff]"
                              : "text-gray-600"
                          }
                        />
                      </button>

                      {mobileExpandedSections.company && (
                        <div className="bg-[#fafafa] px-4 pt-3.5 pb-6">
                          <div className="space-y-5">
                            {company.map((item, index) => (
                              <Link
                                key={index}
                                to={item?.href}
                                className="block text-[#595959] text-lg font-medium leading-7 tracking-[-1px] hover:text-gray-800"
                              >
                                {item?.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sticky Bottom CTA */}
                  <div className="bg-gray-50 border-t border-gray-1600 px-4 py-2.5">
                    <div className="flex flex-col items-center gap-2.5">
                      <h3
                        className="text-[#0f0624] text-lg font-medium leading-7 tracking-[-0.5px]"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        Ready to get started?
                      </h3>
                      <Link
                        to="/demo/"
                        className="w-full bg-[#5e44ff] hover:bg-[#5E24FF] rounded-[40px] px-5 py-2 transition-colors"
                      >
                        <span
                          className="block text-center text-white text-lg font-semibold leading-7 tracking-[-0.45px]"
                          style={{ fontFamily: "Manrope, sans-serif" }}
                        >
                          Book a demo
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
        <DemoModal
          isOpen={isOpen}
          hideSuccessModal={location?.pathname?.includes("/pricing")}
          closeModal={closeModal}
          location={location}
        />
      </div>
    </header>
  );
};

export default Header;
